/* eslint-disable */
// src/utils/shareReport.js

import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";

/** Capitaliza la primera letra sin tocar acentos */
const capitalize = (s = "") => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

/** Quita tildes/diacríticos (Miércoles -> Miercoles) */
const removeAccents = (s = "") => s.normalize("NFD").replace(/[\u0300-\u036f]/g, "");

/** Reemplaza caracteres no válidos en nombres de archivo (/, \, :, *, ?, ", <, >, |) */
export function sanitizeFilename(name) {
  return String(name)
    .replace(/[\/\\:*?"<>|]/g, " ") // prohibidos en Windows/macOS
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Construye un nombre de archivo con: "Miercoles 10 Set 2025 - Local <Nombre>.pdf"
 * - fuerza "Set" como abreviatura de setiembre.
 * - sin bullets ni barras, con espacios.
 * - sin acentos en el día (por ej., "Miercoles" en lugar de "Miércoles").
 */
export function buildVisitPdfFilename({
  date,
  localName = "",
  locale = "es-UY",
  stripAccents = true,
} = {}) {
  const d = date ? new Date(date) : new Date();

  const weekdayRaw = d.toLocaleDateString(locale, { weekday: "long" });
  const weekdayCap = capitalize(weekdayRaw);
  const weekday = stripAccents ? removeAccents(weekdayCap) : weekdayCap;

  const day = d.getDate(); // sin cero a la izquierda

  // Abreviaturas personalizadas para asegurar "Set"
  const months = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Set", "Oct", "Nov", "Dic"];
  const month = months[d.getMonth()];
  const year = d.getFullYear();

  const raw = `${weekday} ${day} ${month} ${year} - Local ${localName}`;
  return `${sanitizeFilename(raw)}.pdf`;
}

/**
 * Captura un contenedor del DOM y lo exporta a PDF.
 */
export async function shareVisitAsPDF({
  containerSelector = "#visit-details-printable",
  filename = "recaudación.pdf",
  orientation = "p",
  format = "a4",
  marginPt = 24,
  bgColor = "#ffffff",
  scale = Math.max(2, window.devicePixelRatio || 1),
  // si querés cortar por otros elementos, sumalos separados por coma
  breakSelector = ".vtb",
} = {}) {
  const root = document.querySelector(containerSelector);
  if (!root) throw new Error(`No se encontró ${containerSelector}`);

  // capturamos TODO
  const canvas = await html2canvas(root, {
    scale,
    useCORS: true,
    backgroundColor: bgColor,
    scrollX: -window.scrollX,
    scrollY: -window.scrollY,
    windowWidth:  Math.max(document.documentElement.scrollWidth,  root.scrollWidth),
    windowHeight: Math.max(document.documentElement.scrollHeight, root.scrollHeight),
  });

  const pdf = new jsPDF({ orientation, unit: "pt", format, compress: true });
  const pageW   = pdf.internal.pageSize.getWidth();
  const pageH   = pdf.internal.pageSize.getHeight();
  const usableW = pageW - marginPt * 2;
  const usableH = pageH - marginPt * 2;

  // Escala pt/px para que el ancho del canvas ocupe usableW
  const ptPerPx = usableW / canvas.width;
  const maxSlicePx = Math.floor(usableH / ptPerPx);

  // ---------- 1) Construimos breakpoints seguros (en coordenadas CANVAS PX) ----------
  // offsetTop relativo al contenedor raíz:
  const rootTop = root.getBoundingClientRect().top + window.scrollY;

  const blocks = Array.from(root.querySelectorAll(breakSelector));
  // breakpoints = top de cada bloque (y el 0 y el final como sentinelas):
  const cssDocToCanvas = (cssY) => (cssY * (canvas.height / root.scrollHeight)); // factor Y
  const breaks = [0];
  blocks.forEach(el => {
    const rect = el.getBoundingClientRect();
    const cssY = (rect.top + window.scrollY) - rootTop; // px CSS desde el principio del root
    const yCanvas = Math.max(0, Math.round(cssDocToCanvas(cssY)));
    if (yCanvas > 0 && yCanvas < canvas.height) breaks.push(yCanvas);
  });
  breaks.push(canvas.height);
  // ordenamos y deduplicamos
  breaks.sort((a,b)=>a-b);
  const uniqueBreaks = breaks.filter((v,i,arr)=> i===0 || v!==arr[i-1]);

  // helper: devuelve el mejor corte ≤ target, pero ≥ current+minChunk
  const MIN_CHUNK_PX = 80; // evita páginas casi vacías
  function findBestBreak(currentPx, targetPx) {
    let best = null;
    for (const b of uniqueBreaks) {
      if (b <= targetPx && b >= currentPx + MIN_CHUNK_PX) best = b;
      if (b > targetPx) break;
    }
    return best; // puede ser null
  }

  // ---------- 2) Troceo por rebanadas alineadas a breakpoints ----------
  let startPx = 0;
  let page = 0;
  while (startPx < canvas.height) {
    const isLastPossible = (startPx + maxSlicePx) >= canvas.height;
    let endPx;

    if (isLastPossible) {
      endPx = canvas.height; // última: todo lo que quede
    } else {
      const target = startPx + maxSlicePx;
      const snap   = findBestBreak(startPx, target);
      endPx = snap || target;  // si no hay breakpoint “cerca”, cortamos duro
    }

    const sliceHeightPx = Math.max(1, endPx - startPx); // evitar 0
    // Si un bloque único es más alto que una página, haremos un corte duro en medio.
    // Eso es normal (no hay forma de evitarlo sin reflow del DOM).

    // Canvas offscreen para la rebanada
    const slice = document.createElement("canvas");
    slice.width  = canvas.width;
    slice.height = sliceHeightPx;
    const ctx = slice.getContext("2d");
    ctx.imageSmoothingEnabled = true;

    ctx.drawImage(
      canvas,
      0, startPx, canvas.width, sliceHeightPx,
      0, 0,       canvas.width, sliceHeightPx
    );

    if (page > 0) pdf.addPage();

    const sliceHpt = sliceHeightPx * ptPerPx;
    const targetHpt = Math.min(usableH, sliceHpt); // no estirar más que la caja útil

    // fondo blanco
    pdf.setFillColor(255,255,255);
    pdf.rect(0, 0, pageW, pageH, "F");

    pdf.addImage(
      slice.toDataURL("image/png"),
      "PNG",
      marginPt,
      marginPt,
      usableW,
      targetHpt,
      undefined,
      "FAST"
    );

    startPx = endPx;
    page += 1;
  }

  const safeName = sanitizeFilename(filename).replace(/\.pdf$/i, "") + ".pdf";
  const blob = pdf.output("blob");
  if (navigator.canShare && navigator.canShare({ files: [new File([blob], safeName, { type: "application/pdf" })] })) {
    const file = new File([blob], safeName, { type: "application/pdf" });
    await navigator.share({ files: [file], title: safeName });
  } else {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = safeName;
    a.click();
    URL.revokeObjectURL(url);
  }
  return true;
}

